import { getAuth, signInWithEmailAndPassword, signOut, onIdTokenChanged, setPersistence, browserLocalPersistence, sendPasswordResetEmail } from 'firebase/auth';
import app from './firebaseConfig';

const auth = getAuth(app);

// Ensure session survives page refreshes via localStorage persistence
setPersistence(auth, browserLocalPersistence).catch(() => {});

const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';
const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';

export type AdminUser = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  roles: string[];
};

/**
 * Sign in with email/password via Firebase, then validate against the backend.
 * Stores the Firebase ID token and user info in localStorage on success.
 */
export async function signIn(email: string, password: string): Promise<AdminUser> {
  const credential = await signInWithEmailAndPassword(auth, email, password);
  const idToken = await credential.user.getIdToken();

  const res = await fetch(`${HOSTNAME}/loginapp/auth/login`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    const message = body?.message ?? 'Login failed';
    throw new Error(message);
  }

  const user: AdminUser = {
    ...body.data.user,
    roles: body.data.roles ?? [],
  };

  localStorage.setItem(TOKEN_KEY, idToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  return user;
}

/**
 * Sign out from Firebase and clear stored credentials.
 */
export async function logOut(): Promise<void> {
  await signOut(auth).catch(() => {});
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Returns the stored Firebase ID token, or null if not signed in.
 */
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Returns the stored admin user, or null if not signed in.
 */
export function getStoredUser(): AdminUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Error thrown by authedFetch on 401. Components should not show error
 * notifications for this — App.tsx handles the logout instead.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export class UnauthorizedError extends Error {
  readonly isUnauthorized = true;
  constructor() { super('Session expired. Please log in again.'); this.name = 'UnauthorizedError'; }
}

/**
 * Drop-in replacement for fetch() that:
 * 1. Always gets a fresh token from Firebase (auto-refreshes via refresh token
 *    if the 1-hour ID token has expired — keeping the user logged in).
 * 2. Injects it as the Authorization header, overriding any stale cached value.
 * 3. Throws UnauthorizedError on 401 so callers skip their own error banners.
 */
export async function authedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Get a fresh ID token — Firebase uses the refresh token silently if needed
  let token: string | null = null;
  try {
    const currentUser = auth.currentUser;
    if (currentUser) {
      token = await currentUser.getIdToken(/* forceRefresh */ false);
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      // Firebase hasn't finished restoring the session yet (async init on page
      // refresh). Use the persisted token so the first API calls don't fail
      // silently while Firebase catches up in the background.
      token = getStoredToken();
    }
  } catch {
    // If refresh fails fall back to stored token (will likely 401 and log out)
    token = getStoredToken();
  }

  if (!token) {
    try { window.dispatchEvent(new CustomEvent('wualt:auth:unauthorized')); } catch { /* ignore */ }
    throw new UnauthorizedError();
  }

  const headers = new Headers((init?.headers as HeadersInit | undefined) ?? {});
  headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    try { window.dispatchEvent(new CustomEvent('wualt:auth:unauthorized')); } catch { /* ignore */ }
    throw new UnauthorizedError();
  }
  return res;
}

/**
 * Returns Authorization headers using the stored token.
 * Prefer authedFetch (which uses a live Firebase token) for actual API calls.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Subscribes to Firebase auth token changes.
 * - Calls onTokenRefresh(newToken) when Firebase silently refreshes the token.
 * - Calls onSignOut() when the session expires or is revoked (user becomes null).
 * Returns an unsubscribe function.
 */
export function subscribeToAuthChanges(
  onTokenRefresh: (token: string) => void,
  onSignOut: () => void,
): () => void {
  return onIdTokenChanged(auth, async (user) => {
    if (user) {
      const freshToken = await user.getIdToken();
      localStorage.setItem(TOKEN_KEY, freshToken);
      onTokenRefresh(freshToken);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      onSignOut();
    }
  });
}
